import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, MapPin, Save } from 'lucide-react';
import { catalogApi } from '../api';
import { ENTITY_SCHEMAS } from '../schemas';
import { I18nInput, TextInput } from '../components/Field';
import { ImagePicker } from '../components/ImagePicker';
import { Modal } from '../components/Modal';

const ENTITIES = [['services', 'Servizi'], ['experiences', 'Esperienze'], ['museums', 'Arte & Musei'], ['events', 'Eventi'], ['places', 'Vicino a te']];
const GEO_LEVELS = [['local', 'Locale'], ['regional', 'Regionale'], ['national', 'Nazionale']];
const BADGES = [['', 'Nessuno'], ['novita', 'Novità'], ['in_evidenza', 'In evidenza'], ['partner_premium', 'Partner Premium'], ['sponsor', 'Sponsor']];
const AVAIL = [['always', 'Sempre'], ['seasonal', 'Stagionale'], ['weekend', 'Weekend'], ['on_request', 'Su prenotazione'], ['custom', 'Date personalizzate']];
const RADII = [['places', 'POI / Vicino a te'], ['museums', 'Arte & Musei'], ['experiences', 'Esperienze'], ['services', 'Servizi'], ['outdoor', 'Escursioni & Outdoor'], ['events', 'Eventi']];

const disp = (v) => (v && typeof v === 'object' ? (v.it || Object.values(v)[0] || '') : v || '');
const badgeLabel = (b) => (BADGES.find(([id]) => id === b) || [])[1] || '';

function Labeled({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-paelbi-ink/50">{label}</span>
      {children}
    </label>
  );
}

const selCls = 'w-full rounded-lg border border-paelbi-line bg-white px-3 py-2 text-sm';

function MetaForm({ meta, onChange }) {
  const set = (k, v) => onChange({ ...meta, [k]: v });
  const setGeo = (k, v) => onChange({ ...meta, geo: { ...(meta.geo || {}), [k]: v } });
  const setAvail = (k, v) => onChange({ ...meta, availability: { ...(meta.availability || {}), [k]: v } });
  const geo = meta.geo || {};
  const avail = meta.availability || { type: 'always' };
  return (
    <div className="space-y-4 rounded-xl border border-paelbi-line bg-paelbi-bg/50 p-4">
      <p className="text-sm font-semibold text-paelbi-ink">Impostazioni catalogo</p>
      <div className="grid grid-cols-2 gap-3">
        <Labeled label="Categoria (id filtro)">
          <input data-testid="catalog-field-category" className={selCls} value={meta.category || ''} onChange={(e) => set('category', e.target.value)} />
        </Labeled>
        <Labeled label="Livello geografico">
          <select data-testid="catalog-field-geolevel" className={selCls} value={meta.geo_level || 'local'} onChange={(e) => set('geo_level', e.target.value)}>
            {GEO_LEVELS.map(([id, l]) => <option key={id} value={id}>{l}</option>)}
          </select>
        </Labeled>
        <Labeled label="Regione"><input className={selCls} value={geo.region || ''} onChange={(e) => setGeo('region', e.target.value)} /></Labeled>
        <Labeled label="Provincia"><input className={selCls} value={geo.province || ''} onChange={(e) => setGeo('province', e.target.value)} /></Labeled>
        <Labeled label="Comune"><input className={selCls} value={geo.municipality || ''} onChange={(e) => setGeo('municipality', e.target.value)} /></Labeled>
        <div className="grid grid-cols-2 gap-2">
          <Labeled label="Lat"><input type="number" step="any" className={selCls} value={geo.lat ?? ''} onChange={(e) => setGeo('lat', e.target.value === '' ? null : parseFloat(e.target.value))} /></Labeled>
          <Labeled label="Lng"><input type="number" step="any" className={selCls} value={geo.lng ?? ''} onChange={(e) => setGeo('lng', e.target.value === '' ? null : parseFloat(e.target.value))} /></Labeled>
        </div>
        <Labeled label="Priorità di visualizzazione">
          <input data-testid="catalog-field-priority" type="number" className={selCls} value={meta.display_priority ?? 0} onChange={(e) => set('display_priority', parseInt(e.target.value || '0', 10))} />
        </Labeled>
        <Labeled label="Badge">
          <select data-testid="catalog-field-badge" className={selCls} value={meta.badge || ''} onChange={(e) => set('badge', e.target.value || null)}>
            {BADGES.map(([id, l]) => <option key={id} value={id}>{l}</option>)}
          </select>
        </Labeled>
        <Labeled label="Disponibilità">
          <select data-testid="catalog-field-availability" className={selCls} value={avail.type || 'always'} onChange={(e) => setAvail('type', e.target.value)}>
            {AVAIL.map(([id, l]) => <option key={id} value={id}>{l}</option>)}
          </select>
        </Labeled>
        {(avail.type === 'seasonal' || avail.type === 'custom') && (
          <div className="col-span-2 grid grid-cols-2 gap-2">
            <Labeled label="Inizio (YYYY-MM-DD)"><input className={selCls} value={avail.start || ''} onChange={(e) => setAvail('start', e.target.value)} /></Labeled>
            <Labeled label="Fine (YYYY-MM-DD)"><input className={selCls} value={avail.end || ''} onChange={(e) => setAvail('end', e.target.value)} /></Labeled>
          </div>
        )}
      </div>
    </div>
  );
}

function DataForm({ schema, data, onChange }) {
  const set = (k, val) => onChange({ ...data, [k]: val });
  return (
    <div className="space-y-4">
      {schema.fields.map((f) => {
        const val = data[f.key];
        if (f.type === 'i18n') return <I18nInput key={f.key} label={f.label} value={val} multiline={f.multiline} onChange={(v) => set(f.key, v)} />;
        if (f.type === 'image') return <ImagePicker key={f.key} label={f.label} value={val} onChange={(v) => set(f.key, v)} />;
        return <TextInput key={f.key} label={f.label} value={val} onChange={(v) => set(f.key, v)} />;
      })}
    </div>
  );
}

const emptyEditing = () => ({ data: {}, category: '', geo_level: 'local', geo: {}, display_priority: 0, badge: null, availability: { type: 'always' }, active: true });

function CatalogItems({ entity }) {
  const schema = ENTITY_SCHEMAS[entity];
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => { setItems(await catalogApi.list(entity)); }, [entity]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        data: editing.data, category: editing.category || null, geo_level: editing.geo_level,
        geo: editing.geo || {}, display_priority: editing.display_priority || 0,
        badge: editing.badge || null, availability: editing.availability || { type: 'always' }, active: true,
      };
      if (editing.id) await catalogApi.update(entity, editing.id, body);
      else await catalogApi.create(entity, body);
      setEditing(null);
      await load();
    } catch (e) {
      alert('Errore: ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const remove = async (it) => {
    if (!window.confirm('Eliminare questo elemento dal catalogo globale?')) return;
    await catalogApi.remove(entity, it.id);
    await load();
  };

  return (
    <div>
      <div className="mt-4 flex justify-end">
        <button type="button" data-testid="catalog-new-button" onClick={() => setEditing(emptyEditing())}
          className="inline-flex items-center gap-2 rounded-btn bg-paelbi-brown px-4 py-2.5 text-sm font-semibold text-white hover:bg-paelbi-brown-dark">
          <Plus size={16} /> Nuovo
        </button>
      </div>

      <div className="mt-4 overflow-hidden rounded-2xl border border-paelbi-line bg-white" data-testid="catalog-items-list">
        {items.length === 0 && <p className="p-6 text-sm text-paelbi-ink/50">Nessun elemento nel catalogo globale.</p>}
        {items.map((it, i) => (
          <div key={it.id} data-testid={`catalog-row-${it.id}`} className={`flex items-center gap-3 p-4 ${i > 0 ? 'border-t border-paelbi-line' : ''}`}>
            {it.data?.image && <img src={it.data.image} alt="" className="h-12 w-12 shrink-0 rounded-lg object-cover" />}
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-paelbi-ink">{disp(it.data?.[schema.titleField]) || '(senza titolo)'}</p>
              <p className="flex items-center gap-1 truncate text-xs text-paelbi-ink/50">
                <MapPin size={12} /> {it.geo?.municipality || it.geo?.region || '—'} · {it.category || '—'}
                {it.badge && <span className="ml-1 rounded bg-paelbi-brown/10 px-1.5 py-0.5 text-[10px] font-semibold text-paelbi-brown">{badgeLabel(it.badge)}</span>}
              </p>
            </div>
            <button type="button" data-testid={`catalog-edit-${it.id}`} onClick={() => setEditing({ ...emptyEditing(), ...it, data: { ...it.data } })} className="rounded-lg p-2 text-paelbi-ink/60 hover:bg-paelbi-bg"><Pencil size={16} /></button>
            <button type="button" data-testid={`catalog-delete-${it.id}`} onClick={() => remove(it)} className="rounded-lg p-2 text-red-500 hover:bg-red-50"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? 'Modifica elemento globale' : 'Nuovo elemento globale'} wide
        footer={<>
          <button type="button" onClick={() => setEditing(null)} className="rounded-btn border border-paelbi-line px-4 py-2 text-sm">Annulla</button>
          <button type="button" data-testid="catalog-save-button" onClick={save} disabled={saving} className="rounded-btn bg-paelbi-brown px-5 py-2 text-sm font-semibold text-white disabled:opacity-60">{saving ? 'Salvo...' : 'Salva'}</button>
        </>}>
        {editing && (
          <div className="space-y-5">
            <DataForm schema={schema} data={editing.data} onChange={(d) => setEditing({ ...editing, data: d })} />
            <MetaForm meta={editing} onChange={(m) => setEditing(m)} />
          </div>
        )}
      </Modal>
    </div>
  );
}

function RadiiConfig() {
  const [radii, setRadii] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { catalogApi.getConfig().then((c) => setRadii(c.radii || {})); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const numeric = Object.fromEntries(Object.entries(radii).map(([k, v]) => [k, parseFloat(v) || 0]));
      const c = await catalogApi.updateConfig(numeric);
      setRadii(c.radii);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally { setSaving(false); }
  };

  if (!radii) return <p className="mt-6 text-sm text-paelbi-ink/50">Caricamento...</p>;
  return (
    <div className="mt-6 max-w-lg rounded-2xl border border-paelbi-line bg-white p-6">
      <p className="text-sm text-paelbi-ink/60">Raggio di ricerca (km) per categoria dei contenuti automatici (OpenStreetMap / provider).</p>
      <div className="mt-4 space-y-3">
        {RADII.map(([key, label]) => (
          <div key={key} className="flex items-center justify-between gap-3">
            <span className="text-sm text-paelbi-ink">{label}</span>
            <input data-testid={`radius-${key}`} type="number" min="0" className="w-28 rounded-lg border border-paelbi-line px-3 py-2 text-sm"
              value={radii[key] ?? ''} onChange={(e) => setRadii({ ...radii, [key]: e.target.value })} />
          </div>
        ))}
      </div>
      <button type="button" data-testid="radii-save-button" onClick={save} disabled={saving}
        className="mt-5 inline-flex items-center gap-2 rounded-btn bg-paelbi-brown px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
        <Save size={16} /> {saving ? 'Salvo...' : saved ? 'Salvato ✓' : 'Salva raggi'}
      </button>
    </div>
  );
}

export function GlobalCatalog() {
  const [tab, setTab] = useState('services');
  return (
    <div data-testid="global-catalog-page">
      <div>
        <h1 className="font-display text-2xl text-paelbi-ink sm:text-3xl">Catalogo Globale</h1>
        <p className="text-paelbi-ink/60">Contenuti condivisi gestiti da Domira. Gli host selezionano cosa mostrare.</p>
      </div>

      <div className="mt-6 flex flex-wrap gap-2 border-b border-paelbi-line">
        {[...ENTITIES, ['config', 'Impostazioni raggi']].map(([k, l]) => (
          <button type="button" key={k} data-testid={`catalog-tab-${k}`} onClick={() => setTab(k)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${tab === k ? 'border-paelbi-brown text-paelbi-brown' : 'border-transparent text-paelbi-ink/60'}`}>{l}</button>
        ))}
      </div>

      {tab === 'config' ? <RadiiConfig /> : <CatalogItems entity={tab} key={tab} />}
    </div>
  );
}

export default GlobalCatalog;
